// The month against the goal (gp.pace) as a ScoreCard (BL-189; was GoalPace): what's in so far as the
// figure, against the goal and the working days gone; a bar to the goal with a mark where it should be by
// today; then the pace in words (ahead, behind, too early to tell, no goal set), by how much, where it's
// heading and what's still in review. In a chat, a way into Flowology. views.tsx draws gp.pace with it.
import type { GoalPaceProps } from '../schemas.ts';
import type { ViewProps } from '../host.ts';
import { day, money, plural } from '../format.ts';
import type { LabelKind } from '../components/Label/Label.tsx';
import { ScoreCard, type ScoreCardProps } from '../components/ScoreCard/ScoreCard.tsx';
import { ViewAct } from '../components/view-act.tsx';

const STATUS: Record<GoalPaceProps['status'], [string, LabelKind]> = {
  ahead: ['Ahead of pace', 'now'],
  behind: ['Behind pace', 'bad'],
  too_early: ['Too early to tell', 'muted'],
  no_target: ['No goal set', 'wait'],
};

/** gp.pace's answer as a ScoreCard's parts. */
export function paceCard(p: GoalPaceProps): ScoreCardProps {
  const [word, kind] = STATUS[p.status];
  const target = p.targetCents;
  const rest = [
    p.deltaCents != null && p.status !== 'too_early' ? ` by ${money(Math.abs(p.deltaCents))}` : '',
    p.projectedCents != null ? `. On course for ${money(p.projectedCents)}` : '',
    p.provisionalCents > 0 ? `, ${money(p.provisionalCents)} of it still in review` : '',
    '.',
  ].join('');
  return {
    label: 'The month against the goal',
    figure: {
      label: `${day(p.from).replace(/^\d+ /, '')} so far`,
      value: money(p.totalCents),
      sub: target
        ? `of ${money(target)}, ${p.workingDaysElapsed} of ${p.workingDays} working days`
        : `${plural(p.jobCount, 'job')} invoiced`,
    },
    ...(target != null && target > 0
      ? {
          meter: {
            bar: {
              value: p.totalCents,
              max: target,
              label: 'Revenue against the goal',
              state: p.status === 'behind' ? ('low' as const) : ('ok' as const),
              marks: [{ at: p.expectedCents, label: 'Where it should be by today' }],
            },
          },
        }
      : {}),
    status: { word, kind, rest },
  };
}

/** gp.pace's view: the office draws the card; a chat adds its way into Flowology. */
export function PaceView({ data, host }: ViewProps<GoalPaceProps>) {
  return (
    <ScoreCard
      {...paceCard(data)}
      {...(host.surface === 'chat'
        ? {
            actions: (
              <ViewAct surface="chat" onPress={() => host.open({ kind: 'numbers' })}>
                Open in Flowology
              </ViewAct>
            ),
          }
        : {})}
    />
  );
}
